import React, { useEffect, useState } from "react";
import Layout from "../../components/ui/Layout";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const getImgSrc = (urls) => {
  const raw = urls?.[0];
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  return `https://res.cloudinary.com/dndm9hex2/image/upload/v1733332084/${raw}`;
};

// Deterministic "featured" pattern — every 7th card is large
const isFeatured = (i) => i % 7 === 0;

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const response = await fetch(
          process.env.REACT_APP_BACKEND_URI + "events/getEvents",
        );
        const data = await response.json();
        setEvents(data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoaded(true);
      }
    }
    fetchEvents();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Outfit:wght@300;400;500&display=swap');

        .ev-list { font-family: 'Outfit', sans-serif; background: #f5f4f1; }
        .serif { font-family: 'Cormorant Garamond', serif; }

        /* ── Page header ── */
        .ev-header {
          border-bottom: 1px solid rgba(0,0,0,0.1);
          padding-bottom: 2rem;
          margin-bottom: 3.5rem;
        }

        /* ── Events grid ── */
        .ev-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        /* Featured card spans 2 columns */
        .ev-card.featured { grid-column: span 2; }

        /* ── Card base ── */
        .ev-card {
          position: relative;
          overflow: hidden;
          border-radius: 16px;
          cursor: pointer;
          background: #e8e7e4;
        }

        /* Image fills card */
        .ev-card-img {
          width: 100%;
          display: block;
          object-fit: cover;
          transition: transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .ev-card.featured .ev-card-img { height: 520px; }
        .ev-card:not(.featured) .ev-card-img { height: 360px; }

        /* Gradient overlay always present, strengthens on hover */
        .ev-card-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(10,26,36,0.82) 0%, rgba(10,26,36,0.2) 45%, transparent 100%);
          transition: opacity 0.4s ease;
        }

        /* Text anchored to bottom */
        .ev-card-body {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1.5rem 1.75rem;
          transform: translateY(4px);
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .ev-card:hover .ev-card-img { transform: scale(1.06); }
        .ev-card:hover .ev-card-body { transform: translateY(0); }

        /* Card label pill */
        .ev-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,255,255,0.12);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 999px;
          padding: 3px 12px;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.75);
          font-family: 'Outfit', sans-serif;
          font-weight: 400;
          margin-bottom: 0.5rem;
        }

        /* No-image fallback card */
        .ev-card-placeholder {
          width: 100%;
          height: 360px;
          background: linear-gradient(135deg, #c8cdd1 0%, #b0b8be 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Empty state */
        .ev-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 5rem 0;
          color: #999;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          font-size: 1rem;
        }

        @media (max-width: 1024px) {
          .ev-grid { grid-template-columns: repeat(2, 1fr); }
          .ev-card.featured { grid-column: span 2; }
          .ev-card.featured .ev-card-img { height: 420px; }
        }

        @media (max-width: 640px) {
          .ev-grid { grid-template-columns: 1fr; gap: 14px; }
          .ev-card.featured { grid-column: span 1; }
          .ev-card.featured .ev-card-img,
          .ev-card:not(.featured) .ev-card-img { height: 280px; }
        }
      `}</style>

      <div className="ev-list w-full min-h-screen">
        <Layout>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
            {/* ── Header ── */}
            <motion.div
              className="ev-header"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex items-end justify-between">
                <div>
                  <p
                    className="text-[#406580] text-xs tracking-[0.25em] uppercase mb-2"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Marrocco Art
                  </p>
                  <h1
                    className="serif text-gray-900"
                    style={{
                      fontSize: "clamp(2.5rem, 5vw, 4rem)",
                      fontWeight: 300,
                      lineHeight: 1,
                    }}
                  >
                    Eventi
                  </h1>
                </div>

                {events.length > 0 && (
                  <span
                    className="text-gray-400 text-sm pb-1"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 300,
                    }}
                  >
                    {events.length} {events.length === 1 ? "evento" : "eventi"}
                  </span>
                )}
              </div>
            </motion.div>

            {/* ── Grid ── */}
            {!loaded ? (
              <div className="flex justify-center py-32">
                <div className="w-10 h-10 border border-[#406580]/30 border-t-[#406580] rounded-full animate-spin" />
              </div>
            ) : (
              <div className="ev-grid">
                {events.length > 0 ? (
                  events.map((event, i) => (
                    <motion.div
                      key={event.id ?? i}
                      custom={i}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-40px" }}
                      variants={fadeUp}
                      className={`ev-card${isFeatured(i) ? " featured" : ""}`}
                    >
                      <Link to={`/eventi/${event.eventId}`} className="block">
                        {getImgSrc(event.eventImagesUrls) ? (
                          <img
                            src={getImgSrc(event.eventImagesUrls)}
                            alt={event.title}
                            className="ev-card-img"
                            loading="lazy"
                          />
                        ) : (
                          <div className="ev-card-placeholder">
                            <svg
                              width="36"
                              height="36"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="rgba(255,255,255,0.4)"
                              strokeWidth="1.5"
                            >
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                        )}

                        <div className="ev-card-overlay" />

                        <div className="ev-card-body">
                          <div className="ev-label">
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 8 8"
                              fill="currentColor"
                            >
                              <circle cx="4" cy="4" r="3" />
                            </svg>
                            Evento
                          </div>
                          <h3
                            className="serif text-white"
                            style={{
                              fontSize: isFeatured(i) ? "1.9rem" : "1.35rem",
                              fontWeight: 300,
                              lineHeight: 1.15,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {event.title}
                          </h3>
                        </div>
                      </Link>
                    </motion.div>
                  ))
                ) : (
                  <div className="ev-empty">
                    <p className="serif italic" style={{ fontSize: "1.4rem" }}>
                      Nessun evento disponibile.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Layout>
      </div>
    </>
  );
}
